import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Radar, Globe, AlertTriangle, Plus, Loader2,
  ChevronRight, Shield, Zap
} from "lucide-react";

const RegulatoryRadarPage = () => {
  const { t } = useTranslation(['modules', 'common']);
  const [watchlist, setWatchlist] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [sectorFilter, setSectorFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [assessForm, setAssessForm] = useState({ standard: "", change_description: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ standard: "", body: "", description: "", impact: "medium", sectors: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sectorFilter) params.set("sector", sectorFilter);
      const [wRes, aRes] = await Promise.all([
        fetch(`${API}/sentinel/regulatory/watchlist?${params}`, { credentials: "include" }),
        fetch(`${API}/sentinel/regulatory/assessments`, { credentials: "include" }),
      ]);
      if (wRes.ok) setWatchlist((await wRes.json()).watchlist || []);
      if (aRes.ok) setAssessments((await aRes.json()).assessments || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [sectorFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runAssessment = async () => {
    if (!assessForm.standard) return;
    setAssessing(true);
    try {
      const res = await fetch(`${API}/sentinel/regulatory/impact-assessment`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(assessForm),
      });
      if (res.ok) { fetchData(); setAssessForm({ standard: "", change_description: "" }); }
    } catch (e) { console.error(e); }
    setAssessing(false);
  };

  const addWatch = async () => {
    try {
      const body = { ...addForm, sectors: addForm.sectors.split(",").map(s => s.trim()).filter(Boolean) };
      await fetch(`${API}/sentinel/regulatory/watchlist`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      fetchData();
      setShowAddForm(false);
      setAddForm({ standard: "", body: "", description: "", impact: "medium", sectors: "" });
    } catch (e) { console.error(e); }
  };

  const impactColor = { high: "text-red-400 bg-red-500/10 border-red-500/30", medium: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30", low: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" };

  if (loading) return <QMSLayout title={t('platform:regulatoryRadar.title')}><div className="flex justify-center h-64 items-center"><Loader2 className="w-8 h-8 animate-spin text-[#00C8E0]" /></div></QMSLayout>;

  return (
    <QMSLayout title={t('platform:regulatoryRadar.title')}>
      <div className="space-y-6" data-testid="regulatory-radar-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>{t('modules:regulatory.title')}</h1>
            <p className="text-slate-400 text-sm mt-1">FDA, ISO, IEC standards monitoring and impact assessment</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} className="bg-[#132340] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none" data-testid="reg-sector-filter">
              <option value="">{t('modules:regulatory.allRegions')}</option><option value="medical">Medical</option><option value="aerospace">Aerospace</option><option value="automotive">Automotive</option><option value="lab">Lab</option>
            </select>
            <button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-1 px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-[3px]" data-testid="add-watch-btn">
              <Plus className="w-3.5 h-3.5" />Add
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="bg-[#132340] border border-emerald-500/20 rounded-[3px] p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-slate-400 text-xs mb-1 block">Standard</label><input type="text" value={addForm.standard} onChange={(e) => setAddForm({ ...addForm, standard: e.target.value })} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none" /></div>
              <div><label className="text-slate-400 text-xs mb-1 block">Regulatory Body</label><input type="text" value={addForm.body} onChange={(e) => setAddForm({ ...addForm, body: e.target.value })} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none" /></div>
            </div>
            <div><label className="text-slate-400 text-xs mb-1 block">Description</label><input type="text" value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-slate-400 text-xs mb-1 block">Impact</label><select value={addForm.impact} onChange={(e) => setAddForm({ ...addForm, impact: e.target.value })} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none"><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
              <div><label className="text-slate-400 text-xs mb-1 block">Sectors (comma-sep)</label><input type="text" value={addForm.sectors} onChange={(e) => setAddForm({ ...addForm, sectors: e.target.value })} placeholder="medical, aerospace" className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none" /></div>
            </div>
            <button onClick={addWatch} className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-[3px]">Save to Watchlist</button>
          </div>
        )}

        {/* Watchlist */}
        <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="watchlist-panel">
          <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-4 flex items-center gap-2" style={{ fontFamily: "'Syne', sans-serif" }}>
            <Globe className="w-5 h-5 text-[#00C8E0]" />Regulatory Watchlist ({watchlist.length})
          </h3>
          <div className="space-y-2">
            {watchlist.map((w, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-bold">{w.standard}</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-[3px] border uppercase ${impactColor[w.impact]}`}>{w.impact}</span>
                    <span className="text-xs text-slate-500 bg-[#132340] px-2 py-0.5 rounded">{w.region}</span>
                  </div>
                  <div className="text-slate-400 text-xs mt-0.5">{w.description}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{w.body} | Last Update: {w.last_update}</div>
                </div>
                <button onClick={() => setAssessForm({ standard: w.standard, change_description: w.description })} className="text-xs text-[#00C8E0] hover:text-white px-3 py-1 border border-[#1E3A5F] rounded-[3px]" data-testid={`assess-${i}`}>
                  Assess Impact
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Impact Assessment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="impact-form">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-4 flex items-center gap-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              <Zap className="w-5 h-5 text-[#00C8E0]" />Run Impact Assessment
            </h3>
            <div className="space-y-3">
              <div><label className="text-slate-400 text-xs mb-1 block">Standard</label>
                <input type="text" value={assessForm.standard} onChange={(e) => setAssessForm({ ...assessForm, standard: e.target.value })} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none" data-testid="assess-standard" /></div>
              <div><label className="text-slate-400 text-xs mb-1 block">Change Description</label>
                <textarea value={assessForm.change_description} onChange={(e) => setAssessForm({ ...assessForm, change_description: e.target.value })} rows={3} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] outline-none resize-none" data-testid="assess-desc" /></div>
              <button onClick={runAssessment} disabled={assessing} className="w-full py-2 bg-[#1A6BCC] text-white text-xs font-bold uppercase rounded-[3px] disabled:opacity-50" data-testid="run-assessment-btn">
                {assessing ? t('operations:regulatoryRadar.assessing') : t('operations:regulatoryRadar.runImpact')}
              </button>
            </div>
          </div>

          <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="assessments-list">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-4">Assessment History</h3>
            {assessments.length === 0 ? <p className="text-slate-500 text-xs">No assessments yet.</p> : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {assessments.map((a) => (
                  <div key={a.assessment_id} className="p-3 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white text-xs font-bold">{a.standard}</span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-[3px] border uppercase ${impactColor[a.risk_level]}`}>{a.risk_level}</span>
                    </div>
                    <div className="text-slate-400 text-xs">{a.gap_count} modules affected</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {a.affected_modules?.map((m) => <span key={m} className="text-xs bg-[#132340] text-slate-400 px-1.5 py-0.5 rounded">{m}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default RegulatoryRadarPage;
